import { FilterOperator, useSearchExamCenterRoomsQuery } from '@exam-center/graphql';
import { useNavigate, useParams } from '@exam-center/router';
import {
  type DataGridProps,
  type GridEventListener,
  type GridRowParams,
  NoRowsOverlay,
  PlaceOutlinedIcon,
} from '@shared/components';
import { ORDER_TYPE } from '@shared/graphql';
import { useMemo } from 'react';

import { BaseExamLocationDataGrid } from '../../components';
import { useExamLocationListSearchParams } from '../../hooks';
import { type ExamLocationRow } from '../../types';

import { examCenterLocationColumns } from './columns';

export default function ExamCenterLocationList() {
  const navigate = useNavigate();
  const { examCenterCode } = useParams('/:examCenterCode/exam-location/locations');

  const [
    { page, pageSize, sortOrder, locationName, provinceId },
    { changePageNumber, changePageSize, changeSorting },
  ] = useExamLocationListSearchParams();

  const isSearchingOrFiltering = locationName.length > 0 || provinceId.length > 0;

  const { data: searchRoomData, loading: isSearchingRoom } = useSearchExamCenterRoomsQuery({
    variables: {
      searchExamCenterRoomsInput: {
        examCenterCode,
        paging: {
          pageSize,
          currentPage: page,
        },
        order: {
          field: 'location',
          type: ORDER_TYPE[sortOrder],
        },
        filter: {
          searchText: {
            fields: ['location'],
            value: locationName,
          },
          conditions: [
            {
              field: 'provinceId',
              operator: FilterOperator.Equal,
              value: provinceId,
            },
            {
              field: 'status',
              operator: FilterOperator.Equal,
              value: 'ACTIVE',
            },
          ],
        },
      },
    },
  });

  const rows = useMemo<ExamLocationRow[]>(() => {
    return (
      searchRoomData?.ecSearchExamCenterRooms.rooms.map(({ room, approvedAt }) => ({
        id: room.id,
        province: room.building.location.province.nameTh,
        locationName: room.building.location.nameTh,
        buildingName: room.building.nameTh,
        roomName: room.name,
        floor: room.floor,
        roomNumber: room.roomNumber ?? '-',
        approvedAt,
      })) ?? []
    );
  }, [searchRoomData]);

  const shouldHideFooter = rows.length === 0;
  const total = searchRoomData?.ecSearchExamCenterRooms.total ?? 0;

  const handleRowClick: GridEventListener<'rowClick'> = (
    params: GridRowParams<ExamLocationRow>,
  ) => {
    navigate('/:examCenterCode/exam-location/locations/:roomId', {
      params: {
        roomId: params.row.id,
        examCenterCode,
      },
    });
  };

  const handleSortModelChange: DataGridProps['onSortModelChange'] = (sortModel) => {
    const sort = sortModel[0]?.sort ?? 'asc';
    changeSorting({ sortOrder: sort });
  };

  return (
    <BaseExamLocationDataGrid
      rows={rows}
      columns={examCenterLocationColumns}
      hideFooter={shouldHideFooter}
      loading={isSearchingRoom}
      onRowClick={handleRowClick}
      sortingMode='server'
      initialState={{
        sorting: {
          sortModel: [{ field: 'locationName', sort: sortOrder }],
        },
      }}
      onSortModelChange={handleSortModelChange}
      slotProps={{
        footer: {
          total: total,
          pagination: {
            page: page,
            count: total,
            rowsPerPage: pageSize,
            onPageChange: (_event, page) => {
              changePageNumber(page);
            },
            onRowsPerPageChange: (event) => {
              const pageSize = parseInt(event.target.value);
              changePageNumber(0);
              return changePageSize(pageSize);
            },
          },
        },
      }}
      slots={{
        noRowsOverlay: () => {
          if (isSearchingOrFiltering) {
            return <NoRowsOverlay />;
          }

          return <NoRowsOverlay icon={PlaceOutlinedIcon} title='ยังไม่มีข้อมูลสถานที่สอบ' />;
        },
      }}
    />
  );
}
